// One game's facts, from its record, its parsed moves, its stage split and,
// when there is one, its complete scan. The record's facts are read here; the
// engine's are read by the fact extractors (`facts/`), one concern per file,
// with the review's own rules so the dashboard and the review never disagree
// about a game.
import { type Color, type MistakeRules, type Move, type Scan, type ReviewPhases, type GameSummary, type FilterableGame } from '@chess/api-model';
import { reviewPath } from '@chess/api-games';
import { endingOf, openingFamilyOf, openingVariationOf, ownerColour, ownerOutcome, ratingBandOf, timeClassOf } from '@chess/api-games/filter';
import { ScannedGame, type FactExtractor } from './extractor.ts';
import { clocksOf } from '@chess/api-review';
import { FACT_EXTRACTORS } from './facts/index.ts';
import { type GameFacts } from '@chess/api-model';

export { HELD_PAWNS, SCORE_CAP, WINNING_PAWNS } from './extractor.ts';

export interface FactsInput {
  record: FilterableGame & { id: string; playedAt: string; pgn: string; source?: GameSummary['source'] };
  moves: Move[];
  phases: ReviewPhases;
  /** Used only when complete. */
  scan?: Scan;
  /** The user's blunder and miss rules; the defaults when left out. */
  rules?: MistakeRules;
}

/**
 * The game's facts; undefined when the reader played neither side or the game
 * is unfinished, the games the library's filter never matches either. An
 * extractor that throws leaves its fields out and is reported through
 * `onFailure`; the rest of the facts stand.
 */
export function gameFacts(
  { record, moves, phases, scan, rules }: FactsInput,
  extractors: readonly FactExtractor[] = FACT_EXTRACTORS,
  onFailure?: (extractor: FactExtractor, error: unknown) => void,
): GameFacts | undefined {
  const reader: Color | undefined = ownerColour(record);
  const outcome = ownerOutcome(record);
  if (!reader || !outcome) return undefined;
  const opponent = reader === 'white' ? record.black : record.white;
  const own = reader === 'white' ? record.white : record.black;
  const facts: GameFacts = {
    id: record.id,
    playedAt: Date.parse(record.playedAt),
    colour: reader,
    outcome,
    timeClass: timeClassOf(record.timeControl),
    readerRating: own.rating,
    opponentRating: opponent.rating,
    ratingBand: ratingBandOf(record),
    ending: endingOf(record.pgn) ?? 'other',
    opening: openingFamilyOf(record.pgn),
    variation: openingVariationOf(record.pgn),
    opponent: opponent.name,
    reviewPath: record.source ? reviewPath({ ...record, source: record.source }) : undefined,
    scanned: false,
  };
  if (!scan?.complete) return facts;

  facts.scanned = true;
  const game = new ScannedGame(moves, phases, scan, reader, rules, clocksOf(record.pgn));
  for (const extractor of extractors) {
    try {
      extractor.extract(game, facts);
    } catch (error) {
      onFailure?.(extractor, error);
    }
  }
  return facts;
}
